import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import { Vehicle } from 'core/models/vehicle';
import { Project } from 'core/models/project';
import { Environment } from 'core/models/environment';
import { useAuthTokenRepository } from 'services/useAuthTokenRepository';
import { MediaClient } from '@tier4/webauto-media-client';
import { TelephoneState } from 'components/views/molecules/Cockpit/TelephoneButton';
import tel03 from './tel03.mp3'; // free sound presented by http://www.kurage-kosho.info/

type OperatorCallProps = {
    vehicleId: Vehicle.Id;
    projectId: Project.Id;
    environmentId: Environment.Id;
    state: TelephoneState;
    setState: (state: TelephoneState) => void;
}

export function OperatorCall(props: OperatorCallProps) {
    const { vehicleId, projectId, environmentId, state, setState } = props;

    const audioRef = useRef<HTMLVideoElement | null>(null);

    const authTokenRepos = useAuthTokenRepository();

    useEffect(() => {
        const getter = async () => ({ token: await authTokenRepos.get(), expiredAt: new Date() });
        MediaClient.addAuthTokenCallback(getter);
        return () => MediaClient.removeAuthTokenCallback(getter);
    }, [authTokenRepos]);

    const [call, setCall] = useState<MediaClient.RemoteCall>();
    const [ringing, setRinging] = useState<MediaClient.Ringing>();
    useEffect(() => {
        if (state != 'disabled') return;

        const call = MediaClient.createRemoteCall({ vehicleId, projectId, environmentId, role: 'operator' }, setRinging);
        setCall(call);

        setState('ready');
    }, [environmentId, projectId, setState, state, vehicleId]);

    const ringtone = useMemo(() => {
        const audio = new Audio(tel03);
        audio.loop = true;
        audio.muted = true;
        return audio;
    }, []);

    const [stream, setStream] = useState<MediaStream>();
    const [talking, setTalking] = useState<MediaClient.Conversation>();
    const [calling, setCalling] = useState<MediaClient.Calling>();

    useEffect(() => {
        if (!ringing) return;
        if (calling || talking || stream) return;

        let cancel = false;

        switch (state) {
            case 'ready':
                setState('ringing');
                break;

            case 'ringing':
                (async (ringing, isCancel) => {
                    try {
                        ringtone.muted = false;
                        await ringtone.play();
                    } catch (e) {
                        console.error(e);
                    }

                    try {
                        await ringing.waitForCancel();
                    } catch (e) {
                        console.warn(`done for cancel: ${JSON.stringify(e)}`);
                    }
                    ringtone.pause();
                    if (isCancel()) return;

                    setRinging(undefined);
                    setState('ready');
                })(ringing, () => cancel);
                break;

            case 'talking':
                (async (ringing, isCancel) => {
                    const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
                    if (isCancel()) {
                        stream.getTracks().forEach(t => t.stop());
                        // ringing.refuse();
                        return;
                    }

                    ringtone.pause();
                    const talking = await ringing.answer(stream);
                    if (isCancel()) {
                        talking.hangUp();
                        stream.getTracks().forEach(t => t.stop());
                        return;
                    }
                    setStream(stream);
                    setRinging(undefined);

                    setTalking(talking);
                })(ringing, () => cancel);
                break;
        }

        return () => {
            cancel = true;
        };
    }, [calling, ringing, ringtone, setState, state, stream, talking]);

    useEffect(() => {
        if (state != 'calling') return;
        if (!call) return;

        let cancel = false;
        (async (isCancel) => {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
            if (isCancel()) {
                return;
            }
            setStream(stream);

            const calling = call.call('passenger', stream);
            setCalling(calling);

            try {
                const talking = await calling.waitForAnswer();
                if (isCancel()) {
                    talking.hangUp();
                    stream.getTracks().forEach(t => t.stop());
                    return;
                }
                setCalling(undefined);
                setState('talking');
                setTalking(talking);
            } catch (error) {
                if (error instanceof MediaClient.RemoteCall.Canceled) {
                    console.info('canceled by the user');
                } else if (error instanceof MediaClient.RemoteCall.Refused) {
                    console.info('refused by the other party');
                } else {
                    console.error('unexpected exception on call', error);
                }
                stream.getTracks().forEach(t => t.stop());
                setStream(undefined);
                setCalling(undefined);
                setState('ready');
            }
        })(() => cancel);

        return () => {
            cancel = true;
        };
    }, [call, setState, state]);

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        if (!talking) return;
        if (!stream) return;

        if (state != 'talking') return;

        let cancel = false;
        (async (talking, isCancel) => {
            audio.srcObject = talking.stream;
            try {
                audio.muted = false;
                await audio.play();
            } catch (error) {
                console.error(`cannot play coming stream: ${JSON.stringify(error)}`);
            }
            if (isCancel()) {
                talking.hangUp();
                stream.getTracks().forEach(t => t.stop());
                return;
            }

            try {
                await talking.waitForHangUp();
            } catch (e) {
                console.debug(e);
            }
            stream.getTracks().forEach(t => t.stop());
            if (isCancel()) {
                return;
            }
            setStream(undefined);
            setTalking(undefined);
            setState('ready');
        })(talking, () => cancel);

        return () => {
            cancel = true;
        };
    }, [setState, state, stream, talking]);

    useEffect(() => {
        if (state != 'ready') return;

        if (calling) {
            calling.cancel();
            setCalling(undefined);
        }

        if (talking) {
            talking.hangUp();
            setTalking(undefined);
        }

        if (stream) {
            stream.getTracks().forEach(t => t.stop());
            setStream(undefined);
        }

    }, [state, calling, talking, stream]);

    return state == 'talking'
        ? <audio ref={audioRef} muted={true} style={{ position: 'absolute', top: 0, left: 0, width: '640px', height: 'auto' }} />
        : <></>;
}

function switchState(currentState: TelephoneState) {
    switch (currentState) {
        case 'disabled':
            return 'disabled';
        case 'ready':
            // make a call
            return 'calling';
        case 'calling':
            // cancel ongoing call
            return 'ready';
        case 'talking':
            // hangup the call
            return 'ready';
        case 'ringing':
            // pickup the call
            return 'talking';
    }
}

export function useOperatorCall() {
    const [state, setState] = useState<TelephoneState>('disabled');

    const toggleState = useCallback(() => {
        setState(switchState(state));
    }, [state, setState]);

    return {
        state,
        setState,
        toggleState,
    };
}
